import type { FeatureCollection } from 'geojson';
import type { GeoJSONSource } from 'maplibre-gl';
import { useEffect } from 'react';
import { useMap } from './MapView';

export interface PlaceGeom {
  id: string;
  name: string;
  type: string;
  shape: 'CIRCLE' | 'POLYGON';
  centerLat: number;
  centerLon: number;
  radiusM: number | null;
  polygon: number[][][] | null;
  color: string | null;
}

/** Approximates a circle as a 48-point polygon (meters → degrees). */
export function circlePolygon(lon: number, lat: number, radiusM: number, points = 48): number[][] {
  const out: number[][] = [];
  const dLat = radiusM / 111_320;
  const dLon = radiusM / (111_320 * Math.cos((lat * Math.PI) / 180));
  for (let i = 0; i <= points; i++) {
    const a = (i / points) * 2 * Math.PI;
    out.push([lon + dLon * Math.cos(a), lat + dLat * Math.sin(a)]);
  }
  return out;
}

export function placesToGeoJSON(places: PlaceGeom[], selectedId?: string | null): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: places.map((p) => ({
      type: 'Feature',
      id: p.id,
      properties: { id: p.id, name: p.name, type: p.type, color: p.color ?? '#14532D', selected: p.id === selectedId ? 1 : 0 },
      geometry: { type: 'Polygon', coordinates: p.shape === 'CIRCLE' ? [circlePolygon(p.centerLon, p.centerLat, p.radiusM ?? 100)] : (p.polygon ?? [[]]) },
    })),
  };
}

const SRC = 'places';

/** Renders places (fill + outline + label) and reports clicks. */
export function PlacesLayer({ places, selectedId, onClick }: { places: PlaceGeom[]; selectedId?: string | null; onClick?: (id: string) => void }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    const data = placesToGeoJSON(places, selectedId);
    const src = map.getSource(SRC) as GeoJSONSource | undefined;
    if (src) {
      src.setData(data);
      return;
    }
    map.addSource(SRC, { type: 'geojson', data });
    map.addLayer({ id: `${SRC}-fill`, type: 'fill', source: SRC, paint: { 'fill-color': ['get', 'color'], 'fill-opacity': ['case', ['==', ['get', 'selected'], 1], 0.35, 0.15] } });
    map.addLayer({ id: `${SRC}-line`, type: 'line', source: SRC, paint: { 'line-color': ['get', 'color'], 'line-width': ['case', ['==', ['get', 'selected'], 1], 3, 1.5] } });
    map.addLayer({
      id: `${SRC}-label`,
      type: 'symbol',
      source: SRC,
      layout: { 'text-field': ['get', 'name'], 'text-size': 12, 'text-font': ['Noto Sans Regular'], 'text-anchor': 'center' },
      paint: { 'text-color': '#0F2A1C', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
    });
    const handler = (e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {
      const id = e.features?.[0]?.properties?.['id'] as string | undefined;
      if (id) onClick?.(id);
    };
    map.on('click', `${SRC}-fill`, handler);
    map.on('mouseenter', `${SRC}-fill`, () => (map.getCanvas().style.cursor = 'pointer'));
    map.on('mouseleave', `${SRC}-fill`, () => (map.getCanvas().style.cursor = ''));
    return () => {
      map.off('click', `${SRC}-fill`, handler);
    };
  }, [map, places, selectedId, onClick]);

  return null;
}
